import { MyVesselsRouteConstants } from './../../../constants/route.constants';
import { PLATFORM, autoinject, computedFrom, useView } from "aurelia-framework";
import { Router } from 'aurelia-router';
import { TaskService } from '@rightship-tech/tasks-ui/services/task.service';
import { CorrespondenceClientModel, CorrespondenceEmailModel, GetCorrespondenceResponse, VesselCorrespondence } from "@rightship-tech/tasks-ui/models/tasks.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { myVesselsDialogConstants } from "../../../constants/dialog.constants";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { AttachmentDialogModel } from "@rightship-tech/common-ui/models/attachments.models";
import { EntityAttachmentManager } from "@rightship-tech/common-ui/services/attachments/entity-attachment-manager";
import * as moment from 'moment';
import * as Enumerable from "linq";
import { EntityTypes } from '@rightship-tech/common-ui/models/basic.models';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { CookieHelper } from '@rightship-tech/common-ui/helpers/cookie-helper';
import { EncodingHelper } from '@rightship-tech/common-ui/helpers/encoding.helper';

@autoinject
@useView(PLATFORM.moduleName("./rfi-correspondence.html"))
export class RfiCorrespondence {

    public task: { taskId: number, entityType: EntityTypes, vesselName: string, vesselImo: number, subtaskId:number, description: string };
    public search: string = "";
    public label: string = "";
    public loadingThreads: boolean = true;
    public loadingMessages: boolean;
    public threads: VesselCorrespondence[] = [];
    public filteredThreads: VesselCorrespondence[] = [];
    public messages: CorrespondenceClientModel[] = [];
    public message: string = "";
    public selectedThread: GetCorrespondenceResponse;
    public selectedThreadMobile: boolean;
    public messagesScrollElem: HTMLElement;
    public addedPeople: CorrespondenceEmailModel[] = [];
    public addedPeopleVisible: boolean = false;
    public currentSubtaskCorrespondenceId: number;
    public attachmentsModelToBeAdded: AttachmentDialogModel[] = [];
    public selectedThreadName: string;

    @computedFrom("selectedThread.entityTypeId")
    get selectedThreadType() {
        if(this.selectedThread?.entityTypeId && (this.selectedThread.entityTypeId == EntityTypes.PSC || this.selectedThread.entityTypeId == EntityTypes.Incident)) {
            return EntityTypes[this.selectedThread.entityTypeId];
        }
    }

    @computedFrom("task", "task.entityType")
    get isCrewWelfare() {
        if(this.task.entityType == EntityTypes.CrewWelfare) {
            return true;
        }
    }

    constructor(private router: Router, private taskService: TaskService, private dialogClientService: DialogClientService, 
        private notificationClientService: NotificationClientService, public entityAttachmentManager:EntityAttachmentManager,
        private configService: ConfigService) { }

    activate(task: {taskId: number, entityType: EntityTypes, vesselName: string, vesselImo: number, subtaskId: number, description: string}) {
        
        //Check cookie
        var key = `queryString-${this.configService.config.environment}`;
		var queryString = CookieHelper.readCookie(key);
        
		if(queryString && queryString != "") {

			const urlParams = new URLSearchParams(queryString);
			if(urlParams.get("entityType") && urlParams.get("entityType") != "") {
                task.entityType = parseInt(EncodingHelper.decodeUrl(urlParams.get("entityType"))) as EntityTypes;
            }
			if(urlParams.get("taskId") && urlParams.get("taskId") != "") {
                task.taskId = parseInt(EncodingHelper.decodeUrl(urlParams.get("taskId")));
            }
			if(urlParams.get("vesselImo") && urlParams.get("vesselImo") != "") {
                task.vesselImo = parseInt(EncodingHelper.decodeUrl(urlParams.get("vesselImo")));
            }
			if(urlParams.get("vesselName") && urlParams.get("vesselName") != "") {
                task.vesselName = EncodingHelper.decodeUrl(urlParams.get("vesselName"));
            }

            //Delete cookie
            CookieHelper.eraseCookie(key, this.configService.config.environment);
        }

        //Task property
        this.task = task;        
        
        //Label
        this.label = this.removeHtmlTags(`${this.task.vesselName} - ${this.task.vesselImo} -  ${this.task.description}`);
    }

    async attached() {

        //Get task correspondence
        this.threads = await this.taskService.getTaskCorrespondences(this.task.taskId, this.task.entityType, this.task.subtaskId, null, null);

        //Get filtered threads
        this.filterThreads();

        //Select the first thread by default
        if(this.filteredThreads.length > 0) {
            this.selectThread(this.filteredThreads[0]);
        }
        this.loadingThreads = false;
    }

    //Select thread
    async selectThread(thread: VesselCorrespondence, force: boolean = false) {

        //If the thread is already selected, do nothing
        if (this.selectedThread && this.selectedThread.subtaskId == thread.subtaskId && !force) {
            return;
        }

        this.loadingMessages = true;
        this.selectedThreadMobile = true;
        
        //Setting correspondnce response to have the selection set before getting the messages
        let correspondenceResponse: GetCorrespondenceResponse = { subtaskId: thread.subtaskId, subtaskStatusId: null, guid: null, 
            modifiedBy: null, modifiedOn: null, vesselName: null, isInvitation: null, description: null, vesselImo: null, requestorName: null, correspondence: null, subtaskStatusHistory: null, entityTypeId: null };
        this.selectedThread = correspondenceResponse;


        //Get task correspondence
        this.selectedThread = await this.taskService.getTaskCorrespondence(thread.subtaskGuid);

        //Set the header label
        const values = [
            this.selectedThread.vesselName || "",
            this.selectedThread.vesselImo || "",
            this.selectedThread.description || ""
        ];
        const filteredValues = values.filter(value => value.toString().trim() !== "");
        this.label = this.removeHtmlTags(filteredValues.join(" - "));

        this.messages = this.selectedThread.correspondence.reverse();
        this.selectedThreadName = thread.name;

        var emails= Enumerable.from(this.selectedThread.correspondence)
            .orderByDescending(x => x.createdDate)
            .select(x => x.emails)
            .firstOrDefault();
        this.addedPeople = emails ? emails.map(x => {
            return {
                email: x.email,
                emailRecipientTypeId: x.emailRecipientTypeId
            }
        }): [];

        this.loadingMessages = false;

        //Scroll to the bottom of the messages. Set a timeout to allow the DOM to update
        setTimeout(() => {
            this.messagesScrollElem.scrollTop = this.messagesScrollElem.scrollHeight;
        });
    }

    public getThreadTypeAcronym(type: string): string {
        switch (type.toLocaleLowerCase()) {
            case "psc":
                return "PSC";
            case "incident":
                return "INC";
            case "fbr":
                return "FBR";
            case "cfr":
                return "CFR";
            case "cw":
                return "CW";
            default:
                return "RFI";
        }
    }
	
    //Navigate back to previous page
    public navigateBack() {
        this.router.navigate(MyVesselsRouteConstants.Dashboard);
    }

    //Go back to threads list on mobile
    public goBackToThreadsListOnMobile() {
        this.selectedThreadMobile = false;
        this.selectedThread = null;
    }

    //Filter threads by search text
    public filterThreads() {
        var threads = this.threads.filter(x => !x.isInvitation);
        if (this.search)
            threads = threads.filter(x => x.name.toLowerCase().includes(this.search.toLowerCase()));
        this.filteredThreads = threads;
    }

    //Remove html tags from string
    public removeHtmlTags(str: string): string {
        let noTags = str.replace(/<[^>]*>/g, '');
        let noDoubleSpaces = noTags.replace(/\s{2,}/g, ' ');
        return noDoubleSpaces;
    }

    //Send corredpondence
    public async sendCorrespondence() {
        await this.taskService.replyTaskCorrespondence(this.selectedThread.guid, this.currentSubtaskCorrespondenceId, this.message, true, false, false, null, this.addedPeople, null, null, null);
        this.notificationClientService.show("Response was submitted.", "RightShip Operation will review your message and get back to you accordingly.", NotificationType.Success);
        this.message = "";
        this.attachmentsModelToBeAdded = [];
        var thread = this.threads.filter(x => x.subtaskId == this.selectedThread.subtaskId)[0];
        await this.selectThread(thread, true);
    }

    public getMessageDate(date: Date): string {
        if(moment(date).day() == moment().day()) {
            return "Today";
        }
        else {
            return moment(date).format('DD/MM/YYYY');
        }
    }

    public getMessageTime(date: Date): string {
        return moment(date).format('DD MMM YYYY HH:mm');
    }

    public formatDateTime(thread: any): string {
        return moment(thread.date).format('DD MMM YYYY HH:mm');
    }

    public openAttachmentDialog() {
        this.dialogClientService.show(myVesselsDialogConstants.addAttachmentToThisConversationDialog, {
            currentAttachments: this.attachmentsModelToBeAdded.map(x => {
                return {
                    name: x.name,
                    uri: x.uri,
                    entityAttachmentId: x.entityAttachmentId,
                    onDialogDeleted: () => {
                        this.attachmentsModelToBeAdded = this.attachmentsModelToBeAdded.filter(a => a.entityAttachmentId != x.entityAttachmentId)
                    }
                }
            }),
            entityType: EntityTypes.SubtaskCorrespondence,
            subtaskCorrespondenceId: this.currentSubtaskCorrespondenceId,
            onSave: async () => {
                let response = await this.taskService.replyTaskCorrespondence(this.selectedThread.guid, null, this.message, false, false, false, null, this.addedPeople, null, null, null);
                this.currentSubtaskCorrespondenceId = response.subtaskCorrespondenceId;
                return response.subtaskCorrespondenceId;
            },
            afterSaved: async (saveAttachmentResponse: any) => {
                this.attachmentsModelToBeAdded.push(...saveAttachmentResponse.map(x => {
                    return {
                        name: x.entityAttachmentsUpdated[0].name,
                        path: x.entityAttachmentsUpdated[0].path,
                        entityAttachmentId: x.entityAttachmentsUpdated[0].entityAttachmentId
                    }
                }));
            }
        }, true, false, false, "right");
    }

    //Open add people dialog
    public openAddPeopleDialog() {
        this.dialogClientService.show(myVesselsDialogConstants.addPeopleToThisconversationDialog, {
            addPeopleToConversationFn: this.addPeopleToConversation.bind(this),
            peopleAddedToConversation: this.addedPeople
        },true);
    }

    private addPeopleToConversation(emails: CorrespondenceEmailModel[]) {
        this.addedPeople = emails;
        this.addedPeopleVisible = true;
    }

    @computedFrom("addedPeople", "addedPeople.length")
    get peopleInConversation(): string {
        var to = this.addedPeople.filter(x => x.emailRecipientTypeId == 1).map(x => x.email).join(", ");
        var cc = this.addedPeople.filter(x => x.emailRecipientTypeId == 2).map(x => x.email).join(", ");
        var bcc = this.addedPeople.filter(x => x.emailRecipientTypeId == 3).map(x => x.email).join(", ");
        var html = "";
        if(to)
        {
            html += `<span><b>To:</b> ${to}</span>`;
        }
        if(cc)
        {
            html += `<span><b>Cc:</b> ${cc}</span>`;
        }
        if(bcc)
        {
            html += `<span><b>Bcc:</b> ${bcc}</span>`;
        }
        return html;
    }

    @computedFrom("attachmentsModelToBeAdded", "attachmentsModelToBeAdded.length")
    get attachmentsInConversation(): string {
        return this.attachmentsModelToBeAdded.map(x => x.name).join(", ");
    }

    public getWho(c: CorrespondenceClientModel) {
        if (c.user) {
            return c.user;
        }

        if (c.emails.length > 0) {
            let from = c.emails.filter(x => x.isFrom)[0];
            if (from) return from.email;
            return c.emails[0].email;
        }
        return null;
    }
    
    //Check if the user can navigate back
    public canNavigateBack():boolean {
		return this.router.history && this.router.history.getHistoryIndex() > 0;
	}
}